import React, { useState } from 'react'
import styled from 'styled-components'
import { trackAmplitude } from 'services/amplitude/amplitude'
import { useRevealProps } from '../../MwPage/components/Reveal'
import RichText from '../../MwPage/components/RichText'
import { IDS } from '../../MwPage/content'
import {
  Chip,
  Chips,
  cyan,
  dark,
  Eyebrow,
  Hi,
  iceLine,
  ink,
  Lede,
  onDark,
  paper,
  sans,
  Section,
  SectionHead,
  Stack,
} from '../../MwPage/styled'
import { useFocus } from '../focus'
import { Card, Column, Grid, H2, Kicker, Wrap } from '../styled'

const After = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.6;

  b {
    color: ${ink};
  }
`

// the prototype's .pains: each one on a card behind a red cross
const Pains = styled(Grid)`
  gap: 10px;

  li {
    position: relative;
    padding: 13px 16px 13px 52px;
    border-radius: 16px;
    border: 1.5px solid ${iceLine};
    background: ${paper};
    font: 600 16px/1.4 ${sans};
    color: ${ink};
    list-style: none;
  }

  li::before {
    content: '✕';
    position: absolute;
    left: 15px;
    top: 14px;
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #fdecea;
    color: #c0392b;
    font: 800 11.5px/1 ${sans};
  }
`

// the prototype's .reframe: the turn on a dark card, and the link down to the blocker
const Reframe = styled.div`
  padding: 22px;
  border-radius: 22px;
  background: ${dark};
  color: ${onDark};

  p {
    margin: 0 0 14px;
    font-size: clamp(16px, 2.6vw, 18px);
    line-height: 1.42;
  }

  strong {
    color: #ffffff;
  }

  a {
    color: ${cyan};
    font-weight: 700;
    font-size: 15.5px;
    text-decoration: none;
  }
`

// the pains as a list, with the reframe under them, where a page lists them instead of the tabs
const PainsList = ({ problem }) => (
  <Column $max={820} $gap={16}>
    <Pains as="ul" $min={300} style={{ margin: 0, padding: 0 }} {...useRevealProps()}>
      {problem.pains.map((pain) => (
        <li key={pain}>{pain}</li>
      ))}
    </Pains>
    <Reframe {...useRevealProps()}>
      <p>
        <RichText text={problem.reframe.text} />
      </p>
      <a href={`#${IDS.where}`}>{problem.reframe.link}</a>
    </Reframe>
  </Column>
)

const Tabs = ({ problem }) => {
  const { key: page } = useFocus()
  const [picked, setPicked] = useState(problem.tabs[0].key)
  const tab = problem.tabs.find((item) => item.key === picked)

  // a named event, so the chips can be charted without reading autocaptured click text
  const pick = (item) => {
    if (item.key !== picked) trackAmplitude('problem_selected', { page, problem: item.key, label: item.label })
    setPicked(item.key)
  }

  return (
    <Column $max={820} {...useRevealProps()}>
      <Chips role="group" aria-label={problem.headline}>
        {problem.tabs.map((item) => (
          <Chip key={item.key} type="button" aria-pressed={item.key === picked} onClick={() => pick(item)}>
            <i aria-hidden="true" />
            {item.label}
          </Chip>
        ))}
      </Chips>
      <Card $tint aria-live="polite">
        <Kicker>{problem.panelLabel}</Kicker>
        <p style={{ color: ink }}>{tab.text}</p>
      </Card>
      <After>
        {problem.after[0]}
        <b>{problem.after[1]}</b>
        {problem.after[2]}
      </After>
    </Column>
  )
}

const RevealLede = ({ children }) => <Lede {...useRevealProps()}>{children}</Lede>

/**
 * "Sound familiar?": tap the problem that sounds most like you and read how Jumpstart helps with it, or, where a
 * page lists its pains, the pains and the reframe under them.
 */
const ProblemSection = () => {
  const { problem } = useFocus()

  return (
    <Section $ice={!!problem.pains}>
      <Wrap>
        <Stack $gap="clamp(24px, 3vw, 32px)">
          <SectionHead>
            <Eyebrow {...useRevealProps()}>{problem.eyebrow}</Eyebrow>
            <H2 {...useRevealProps()}>
              {problem.headline} <Hi>{problem.headlineAccent}</Hi>
            </H2>
            {problem.lede && <RevealLede>{problem.lede}</RevealLede>}
          </SectionHead>
          {problem.pains ? <PainsList problem={problem} /> : <Tabs problem={problem} />}
        </Stack>
      </Wrap>
    </Section>
  )
}

export default ProblemSection
